import {useContext, useEffect, useState} from 'react'
import _ from 'lodash'
import {ICategorical} from '@/utils/media'
import {JsonViewer} from '@/components/json-viewer'
import {calculateCatalogStats} from '@/components/catalog'
import {IDataSourceItem, LegacyCatalogContext} from '@/features/data-sources'
import {TabTitle} from '../tab-title'
import {Categoricals, ICategoricalsProps} from './categoricals'
import './style.css'

export interface IMediaDetailsProps<T extends IDataSourceItem> {
  className?: string
  data?: T
  showTitle?: boolean
  showCategoricals?: boolean
  showScore?: boolean
  showJsonViewer?: boolean
  categoricalsProps?: Partial<ICategoricalsProps>
  renderDetails?: (data: T) => React.ReactElement | undefined
  onItemClicked?: (data: T) => void
}

export interface IColorMap {
  default: React.CSSProperties['backgroundColor']
  [key: string]: React.CSSProperties['backgroundColor']
}

export function MediaDetails<T extends IDataSourceItem>(props: IMediaDetailsProps<T>) {
  const {
    className,
    data,
    showTitle,
    showCategoricals,
    showScore,
    showJsonViewer,
    categoricalsProps,
    renderDetails,
    onItemClicked,
  } = {
    showTitle: true,
    ...props,
  }

  const {catalogStats} = useContext(LegacyCatalogContext)
  const {fields} = catalogStats

  const [categories, setCategories] = useState<{[key: string]: ICategorical | string[]}>()

  useEffect(() => {
    const {categories} = calculateCatalogStats(_.compact([data]), {includeAllCategoricals: true})
    setCategories(_.chain(data).pick(_.keys(categories)).value())
  }, [data])

  if (!data) return <div style={{padding: '0 0.5em', opacity: 0.8}}>nothing selected...</div>

  const colorMap: IColorMap = {
    genre: 'rgba(33, 133, 208, 0.8)',
    keyword: 'rgba(163, 51, 200, 0.8)',
    mood: 'rgba(242, 113, 28, 0.8)',
    mood_tag: 'rgba(219, 40, 40, 0.8)',
    default: '#ff099a',
  }

  function handleOnClick() {
    if (!onItemClicked) return
    if (!data) return
    onItemClicked(data)
  }

  const classNames = _.chain(['media-details', className]).compact().join(' ').value()

  return (
    <div className={classNames}>
      {data ?
        <>
          {showTitle ?
            <TabTitle
              data={data}
              onClick={handleOnClick}
            />
          : undefined}
          <div className='content'>
            {renderDetails ? renderDetails(data) : null}
            {showScore && _.has(fields, 'ainar_score') ?
              <Categoricals
                {...categoricalsProps}
                data={{
                  rating: {ainar_score: data.ainar_score},
                }}
                range={{min: 0, max: 10}}
                sourcePath={() => 'ainar_score'}
                wordProps={(descriptor) => {
                  const _wordProps =
                    _.isFunction(categoricalsProps?.wordProps) ?
                      categoricalsProps?.wordProps(descriptor)
                    : categoricalsProps?.wordProps
                  return {
                    ..._wordProps,
                    style: {
                      backgroundColor: colorMap.ainar_score || colorMap.default,
                      ..._wordProps?.style,
                    },
                  }
                }}
              />
            : null}
            {showCategoricals ?
              <Categoricals
                {...categoricalsProps}
                data={categories}
                range={{min: 0, max: 1}}
                wordProps={(descriptor) => {
                  const {kind} = descriptor
                  const color = _.get(colorMap, kind) || undefined // colorMap.default;
                  const _wordProps =
                    _.isFunction(categoricalsProps?.wordProps) ?
                      categoricalsProps?.wordProps(descriptor)
                    : categoricalsProps?.wordProps
                  return {
                    ..._wordProps,
                    style:
                      color ?
                        {
                          backgroundColor: color,
                          ..._wordProps?.style,
                        }
                      : undefined,
                  }
                }}
              />
            : null}
            {showJsonViewer ?
              <JsonViewer
                collapsed={1}
                data={data}
              />
            : null}
          </div>
        </>
      : 'no data'}
    </div>
  )
}
